'use client';

import { createRoute } from '@tanstack/react-router';
import { useTheme } from 'next-themes';
import { Suspense, useEffect, useState } from 'react';

import MarketingLayout from '@/components/layouts/MarketingLayout';
import { Skeleton } from '@/components/ui';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import { seoHead } from '@/lib/seo/routeMeta';
import { theme } from '@/lib/ui/theme';

import { Route as rootRoute } from './__root';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/philosophy',
  head: () => seoHead('/philosophy'),
  component: Philosophy,
});

export const beliefs = [
  {
    title: 'Confidence is not correctness.',
    description:
      "The agent says it's done, it builds, the tests are green. None of that proves the code is right.",
  },
  {
    title: 'The bottleneck moved to verification.',
    description:
      'Generating code stopped being the hard part. Agents write faster than anyone can review by hand. Trusting what they wrote is the new constraint.',
  },
  {
    title: 'Tests that lie are worse than no tests.',
    description:
      'A green suite proves something ran, not that it checked anything. Test quality gets reviewed, not assumed.',
  },
  {
    title: 'The code is the only source of truth.',
    description:
      "Specs and design docs go stale the moment they're written. Mindrealm reviews what actually shipped, not what was promised.",
  },
  {
    title: 'A finding is something to fix.',
    description:
      "Not a comment to skim. If fixing it wouldn't make the code better, it should never have been raised.",
  },
  {
    title: 'Precision over noise.',
    description:
      'An alarm you cannot trust is worse than silence. Every false positive is a bug, not a cost of doing business. That is what makes it safer to let an agent fix findings on its own, with no person checking each one again.',
  },
  {
    title: 'Speed without judgment is debt.',
    description:
      "AI raised throughput, not judgment. Code that ships faster than anyone can verify isn't progress. It's risk compounding.",
  },
];

export const approach = [
  {
    title: 'Many checks, not one oracle.',
    description:
      'Over 75 focused checks, each built for one kind of failure, instead of one model with an opinion on everything.',
  },
  {
    title: 'Determinism: the same code, the same review.',
    description:
      'Run it twice or ten times and the findings are identical. The result rests on what the code structurally is, not on what a model decided that run. So a finding can be acted on the same way every time, without a person checking it again.',
  },
  {
    title: 'Suppressions are explicit, never silent.',
    description:
      'Disagree with a check and you can silence it, but the suppression is recorded and reviewable. Nothing hides a real problem from your team.',
  },
  {
    title: 'Principles are enforced, not suggested.',
    description:
      'Coupling, cohesion, separation of concerns: load-bearing structural decisions, not style preferences.',
  },
];

function PhilosophyContent() {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(appHasMounted());

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  const showLoading = !mounted;
  const isDark = mounted && resolvedTheme === 'dark';

  if (showLoading) {
    return (
      <MarketingLayout isLoading={true} isSignedIn={false}>
        <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
          <div className="mx-auto max-w-[800px]">
            <Skeleton className="mx-auto mt-2 mb-4 h-12 w-[75%]" />
            <Skeleton className="mx-auto mb-12 h-6 w-[60%]" />
            <Skeleton className="mx-auto mb-4 h-10 w-[80%]" />
            <Skeleton className="mx-auto mb-2 h-5 w-[70%]" />
            <Skeleton className="mx-auto mb-16 h-5 w-[55%]" />
            <Skeleton className="mb-6 h-8 w-[40%]" />
            <div className="mb-16 grid gap-6 md:grid-cols-2">
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <Skeleton key={i} className="h-24 w-full rounded-xl" />
              ))}
            </div>
            <Skeleton className="mb-6 h-8 w-[35%]" />
            <div className="grid gap-6 md:grid-cols-2">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-24 w-full rounded-xl" />
              ))}
            </div>
          </div>
        </section>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout isLoading={false} isSignedIn={false}>
      <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
        <div className="mx-auto max-w-[800px]">
          {/* Header */}
          <h1
            className="text-gradient-hero mb-4 text-center text-4xl font-bold tracking-tight md:text-5xl"
            style={{ fontFamily: theme.fonts.display, lineHeight: 1.2 }}
          >
            Judgment enhances generation.
          </h1>
          <p
            className="text-foreground/70 text-center text-lg md:text-xl"
            style={{ fontFamily: theme.fonts.body }}
          >
            AI writes the code. Mindrealm makes sure it's actually correct.
          </p>

          {/* Problem punch */}
          <div className="mt-14 mb-16 text-center">
            <p
              className="text-2xl font-bold md:text-3xl"
              style={{ fontFamily: theme.fonts.display }}
            >
              AI doesn't fail loudly. It fails confidently.
            </p>
            <p
              className="text-foreground/70 mx-auto mt-4 max-w-[600px] text-lg"
              style={{ fontFamily: theme.fonts.body }}
            >
              Someone still has to decide whether the code is right. At the
              speed agents write, that can't be a person reading every line.
            </p>
          </div>

          {/* What we believe */}
          <h2
            className="mb-6 text-2xl font-bold md:text-3xl"
            style={{ fontFamily: theme.fonts.display }}
          >
            What we believe
          </h2>
          <div className="grid gap-x-8 gap-y-6 md:grid-cols-2">
            {beliefs.map((belief, i) => (
              <div key={belief.title} className="flex gap-4">
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-base font-bold"
                  style={{
                    background:
                      theme.box.accentNumber.bg[isDark ? 'dark' : 'light'],
                    color:
                      theme.box.accentNumber.color[isDark ? 'dark' : 'light'],
                  }}
                >
                  {i + 1}
                </span>
                <div>
                  <h3
                    className="mb-1 text-lg font-semibold"
                    style={{ fontFamily: theme.fonts.display }}
                  >
                    {belief.title}
                  </h3>
                  <p
                    className="text-foreground/70 text-base"
                    style={{ fontFamily: theme.fonts.body }}
                  >
                    {belief.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* How we review */}
          <h2
            className="mt-16 mb-6 text-2xl font-bold md:text-3xl"
            style={{ fontFamily: theme.fonts.display }}
          >
            How we review
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            {approach.map((item) => (
              <div
                key={item.title}
                className="rounded-xl border p-5"
                style={{
                  borderColor:
                    theme.box.purple.border[isDark ? 'dark' : 'light'],
                }}
              >
                <h3
                  className="mb-1 text-lg font-semibold"
                  style={{ fontFamily: theme.fonts.display }}
                >
                  {item.title}
                </h3>
                <p
                  className="text-foreground/70 text-base"
                  style={{ fontFamily: theme.fonts.body }}
                >
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          {/* Closing */}
          <div
            className="mt-16 rounded-xl p-6 text-center md:p-8"
            style={{
              background: theme.box.purple.bg[isDark ? 'dark' : 'light'],
              border: `1px solid ${theme.box.purple.border[isDark ? 'dark' : 'light']}`,
            }}
          >
            <h2
              className="mb-4 text-2xl font-bold md:text-3xl"
              style={{ fontFamily: theme.fonts.display }}
            >
              Built by using it
            </h2>
            <p
              className="text-foreground/70 mx-auto mb-4 max-w-[620px]"
              style={{ fontFamily: theme.fonts.body }}
            >
              Every check exists because an agent made that mistake while we
              built Mindrealm, and we got tired of catching it by hand. It comes
              from tens of thousands of lines of logged failures and several
              rebuilds from scratch. This isn't theory.
            </p>
            <p
              className="text-foreground/80 mb-4"
              style={{ fontFamily: theme.fonts.body }}
            >
              Correctness you can verify, not vibes you have to trust.
            </p>
            <p
              className="text-gradient-hero text-xl font-bold md:text-2xl"
              style={{ fontFamily: theme.fonts.display }}
            >
              Now it catches them for you, on every change.
            </p>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

function Philosophy() {
  return (
    <Suspense
      fallback={
        <MarketingLayout isLoading={true} isSignedIn={false}>
          <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
            <div className="mx-auto max-w-[800px]">
              <Skeleton className="mx-auto mb-8 h-12 w-[70%]" />
              <Skeleton className="mx-auto mb-4 h-6 w-[80%]" />
            </div>
          </section>
        </MarketingLayout>
      }
    >
      <PhilosophyContent />
    </Suspense>
  );
}
